import { ApolloClient, ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { questFragment } from '../../client/fragments/quest';
import {
    GetQuestsByIdsQuery,
    GetQuestsByIdsQueryVariables,
    GetSingleQuestQuery,
    GetSingleQuestQueryVariables,
} from '../../client/generated/graphql';
import { getQuestsByIdsQuery } from '../../client/queries/questsByIds';
import { getSingleQuestQuery } from '../../client/queries/singleQuest';
import { ListQuest } from '../../client/types';

type QuestWithOptionalNested = ListQuest & {
    category?: GetSingleQuestQuery['quest']['category'] | null;
    goal?: GetSingleQuestQuery['quest']['goal'] | null;
};

// Read a single quest from normalized cache by ID
export const useCachedQuest = (questId?: string | null): ListQuest | null => {
    const client = useApolloClient();

    if (!questId) return null;

    try {
        const quest = client.cache.readFragment<ListQuest>({
            id: client.cache.identify({ __typename: 'Quest', id: questId }),
            fragment: questFragment,
            fragmentName: 'QuestFields',
        });
        return quest ?? null;
    } catch {
        return null;
    }
};

export const useCachedQuestsMap = (questIds?: string[] | null) => {
    const client = useApolloClient();

    if (!questIds) return {};

    const questMap: Record<string, ListQuest> = {};

    for (const questId of questIds) {
        try {
            const quest = client.cache.readFragment<ListQuest>({
                id: client.cache.identify({ __typename: 'Quest', id: questId }),
                fragment: questFragment,
                fragmentName: 'QuestFields',
            });
            if (quest) {
                questMap[questId] = quest;
            }
        } catch {
            // Quest not in cache, skip it
        }
    }

    return questMap;
};

export const useCachedQuestsMapGenerator = () => {
    const client = useApolloClient();

    return (questIds?: string[] | null) => {
        if (!questIds) return {};

        const questMap: Record<string, ListQuest> = {};

        for (const questId of questIds) {
            try {
                const quest = client.cache.readFragment<ListQuest>({
                    id: client.cache.identify({ __typename: 'Quest', id: questId }),
                    fragment: questFragment,
                    fragmentName: 'QuestFields',
                });
                if (quest) {
                    questMap[questId] = quest;
                }
            } catch {
                // Quest not in cache, skip it
            }
        }

        return questMap;
    };
};

export const readCachedQuests = (client: ApolloClient<object>): ListQuest[] => {
    // Get all Quest objects from cache regardless of which query fetched them
    const cacheData = client.cache.extract();
    return Object.entries(cacheData)
        .filter(([key, value]: [string, any]) => key.startsWith('Quest:') || value?.__typename === 'Quest')
        .map(([_key, value]) => value as ListQuest);
};

export const useCachedQuests = (): ListQuest[] => {
    const client = useApolloClient();

    return useMemo(() => readCachedQuests(client), [client.cache]);
};

export const useCachedQuestWithFallback = (
    questId?: string | null,
): {
    quest: QuestWithOptionalNested | null;
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const cachedQuest = useCachedQuest(questId);

    // If not in cache and we have a questId, fetch from network with all related data
    const {
        data: networkData,
        loading,
        error,
    } = useQuery<GetSingleQuestQuery, GetSingleQuestQueryVariables>(getSingleQuestQuery, {
        variables: { id: questId! },
        skip: !questId || !!cachedQuest,
        fetchPolicy: 'cache-first',
    });

    // Return cached quest if available, otherwise network quest
    const quest = cachedQuest ?? networkData?.quest ?? null;

    return {
        quest,
        loading: loading && !cachedQuest,
        error,
    };
};

export const useCachedQuestsWithFallback = (
    questIds?: string[] | null,
): {
    quests: ListQuest[];
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const client = useApolloClient();

    // Try to read all quests from normalized cache
    const cachedQuests: ListQuest[] = [];
    const missingQuestIds: string[] = [];

    if (questIds && questIds.length > 0) {
        for (const questId of questIds) {
            try {
                const quest = client.cache.readFragment<ListQuest>({
                    id: client.cache.identify({ __typename: 'Quest', id: questId }),
                    fragment: questFragment,
                    fragmentName: 'QuestFields',
                });
                if (quest) {
                    cachedQuests.push(quest);
                } else {
                    missingQuestIds.push(questId);
                }
            } catch {
                missingQuestIds.push(questId);
            }
        }
    }

    // Always call useQuery (hooks must be unconditional)
    const {
        data: networkData,
        loading,
        error,
    } = useQuery<GetQuestsByIdsQuery, GetQuestsByIdsQueryVariables>(getQuestsByIdsQuery, {
        variables: { ids: missingQuestIds },
        skip: !questIds || questIds.length === 0 || missingQuestIds.length === 0,
        fetchPolicy: 'cache-first',
    });

    if (!questIds || questIds.length === 0) {
        return { quests: [], loading: false, error: undefined };
    }

    // Combine cached and network quests
    const allQuests = [...cachedQuests, ...(networkData?.questsByIds ?? [])];

    return {
        quests: allQuests,
        loading,
        error,
    };
};
